import React from 'react'
import { useLocation } from 'react-router-dom'
import ThermometerWithGate from '@/modules/termometro/components/ThermometerWithGate'
import { getThermometerMemberVisible } from '@/modules/settings/services/thermometerFeatureService'
import { CheckCircle, AlertTriangle, AlertCircle, MoreHorizontal } from 'lucide-react'
import { useOportunidadesQuota } from '../hooks/useOportunidadesQuota'
import type { OpportunityItem } from '../types'
import { useOportunidadeModal } from '../context/OportunidadeModalContext'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { isMasterByEmail } from '@/shared/utils/role'
import { useBTCHigherTFWarning } from '../hooks/useBTCHigherTFWarning'
import { Skeleton } from '@/shared/ui/Skeleton'
import BTCWarningCard from './BTCWarningCard'
import OportunidadesFilterDrawer from './OportunidadesFilterDrawer'
import { Button, AsyncContentProgress } from '@/shared/ui'
import LoadingCandles from '@/shared/ui/LoadingCandles'
import { useFilteredOportunidades, type OpportunityEntryZoneMap } from '../hooks/useFilteredOportunidades'
import OpportunityActionSheet from './OpportunityActionSheet'
import { useFocusStore } from '../stores/focusStore'
import ObjectiveIcon from './ObjectiveIcon'
import { getBTCWarningEnabled } from '@/modules/settings/services/btcWarningSettingsService'
import { useSignalsEnabled } from '@/modules/signals/hooks/useSignalsEnabled'
import {
  fetchEntryZoneForOpportunities,
  fetchSignalsMultiTfState,
  fetchSignalsMultiTfDirectionConflicts,
  type OpportunityMultiTfState,
  type OpportunityMultiTfDirectionConflict,
} from '@/modules/signals'
import { SignalCard } from '@/modules/signals/components/SignalCard'
import { OpportunitySignalsRow } from '@/modules/signals/components/OpportunitySignalsRow'
import { OpportunityCommunityPnlRow } from '@/modules/signals/components/OpportunityCommunityPnlRow'
import { useOpportunitySignals } from '@/modules/signals/hooks/useOpportunitySignals'
import { GlowingEffect } from '@/components/ui/glowing-effect'
import { useInOutEnabled } from '@/modules/in-out/hooks/useInOutEnabled'
import { InOutProvider } from '@/modules/in-out/hooks/useInOutStatus'
import { InOutFeedbackProvider } from '@/modules/in-out/context/InOutFeedbackContext'
import { useCommunityEnabled } from '@/modules/comunidade/hooks/useCommunityEnabled'
import { useCommunitySummary } from '@/modules/comunidade/hooks/useCommunitySummary'
import { OpportunityCommunityEntry } from '@/modules/comunidade/components/OpportunityCommunityEntry'
import type { CommunitySummary } from '@/modules/comunidade/services/communityService'
import { useResultsEnabled, useResultsDrawer } from '@/modules/resultados'
import { OpportunityCompactCard } from './OpportunityCompactCard'

type OportunidadesListProps = {
  upgradeHint?: React.ReactNode
  mode?: 'confirmed' | 'in'
  conversationOnly?: boolean
}

export default function OportunidadesList({ upgradeHint, mode = 'confirmed', conversationOnly = false }: OportunidadesListProps) {
  const { items, loading, error, exceeded, nextResetAt } = useOportunidadesQuota()
  const location = useLocation()
  const { openModal } = useOportunidadeModal()
  const btc = useBTCHigherTFWarning(30000)
  const [btcWarningEnabled, setBtcWarningEnabled] = React.useState<boolean | null>(null)
  const [showExceeded, setShowExceeded] = React.useState(false)
  const [filtersOpen, setFiltersOpen] = React.useState(false)
  const [copyModeById, setCopyModeById] = React.useState<Record<string, boolean>>({})
  React.useEffect(() => { if (exceeded) setShowExceeded(true) }, [exceeded])
  // Filtragem client-side (SSOT via store de filtros) + ordenação por data (mais recente primeiro)
  const activeItems = React.useMemo(() => {
    const onlyActive = items.filter(
      (it) => String(it.status || '').toLowerCase() === 'active',
    )
    return [...onlyActive].sort((a, b) => {
      const ta = a.publishedAt ? Date.parse(a.publishedAt) : 0
      const tb = b.publishedAt ? Date.parse(b.publishedAt) : 0
      return tb - ta
    })
  }, [items])
  const [entryZoneMap, setEntryZoneMap] = React.useState<OpportunityEntryZoneMap>({})
  const [multiTfStateMap, setMultiTfStateMap] = React.useState<Record<string, OpportunityMultiTfState['state']>>({})
  const [multiTfDirectionMap, setMultiTfDirectionMap] = React.useState<Record<string, OpportunityMultiTfDirectionConflict>>({})
  const activeIds = React.useMemo(
    () => activeItems.map((it) => Number(it.id)).filter((id) => Number.isFinite(id)),
    [activeItems],
  )
  const { filtered } = useFilteredOportunidades(activeItems, entryZoneMap)
  const communityEnabled = useCommunityEnabled()
  const { map: conversationSummaryMap, loading: conversationSummaryLoading } = useCommunitySummary(
    activeIds,
    conversationOnly ? communityEnabled : false,
  )
  const [conversationSummaryInitialized, setConversationSummaryInitialized] = React.useState(false)

  React.useEffect(() => {
    if (!conversationOnly) return
    if (communityEnabled !== true) {
      setConversationSummaryInitialized(true)
      return
    }
    if (!activeIds.length) {
      setConversationSummaryInitialized(true)
      return
    }
    if (conversationSummaryLoading) return
    setConversationSummaryInitialized(true)
  }, [conversationOnly, communityEnabled, conversationSummaryLoading, activeIds.length])

  const conversationOnlyItems = React.useMemo(() => {
    if (!conversationOnly) return []
    return filtered.filter((it) => {
      const summary = conversationSummaryMap[String(it.id)]
      return !!summary && Number(summary.messagesCount ?? 0) > 0
    })
  }, [conversationOnly, filtered, conversationSummaryMap])
  // Gate do termÃ´metro (membros)
  const { profile, user } = useAuth()
  const currentUserId = user?.id ? String(user.id) : null
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'

  const conversationSections = React.useMemo(() => {
    if (!conversationOnly) {
      return {
        participating: [] as OpportunityItem[],
        others: [] as OpportunityItem[],
      }
    }

    const participating: OpportunityItem[] = []
    const others: OpportunityItem[] = []

    conversationOnlyItems.forEach((it) => {
      const summary = conversationSummaryMap[String(it.id)]
      const participates =
        !!currentUserId &&
        !!summary &&
        Array.isArray(summary.avatars) &&
        summary.avatars.some((a) => String(a.authorId) === currentUserId)

      if (participates) participating.push(it)
      else others.push(it)
    })

    return { participating, others }
  }, [conversationOnly, conversationOnlyItems, conversationSummaryMap, currentUserId])

  const listItems = React.useMemo(() => {
    if (!conversationOnly) return filtered
    return [...conversationSections.participating, ...conversationSections.others]
  }, [conversationOnly, filtered, conversationSections.participating, conversationSections.others])
  const [memberVisible, setMemberVisible] = React.useState<boolean | null>(null)
  React.useEffect(() => {
    let mounted = true
    getThermometerMemberVisible()
      .then((on)=> { if (mounted) setMemberVisible(!!on) })
      .catch(()=> { if (mounted) setMemberVisible(false) })
    return () => { mounted = false }
  }, [])
  React.useEffect(() => {
    let mounted = true
    getBTCWarningEnabled()
      .then((on) => { if (mounted) setBtcWarningEnabled(on) })
      .catch(() => { if (mounted) setBtcWarningEnabled(false) })
    return () => { mounted = false }
  }, [])
  const thermoAllowed = isMaster ? true : !!memberVisible
  const masterNote = isMaster && memberVisible === false ? 'Recurso habilitado somente para usuario master' : undefined

  const [actionOpen, setActionOpen] = React.useState(false)
  const [actionItem, setActionItem] = React.useState<OpportunityItem | null>(null)
  const signalsEnabled = useSignalsEnabled()
  const inOutEnabled = useInOutEnabled()
  const resultsEnabled = useResultsEnabled()
  const { openResultsDrawer } = useResultsDrawer()
  const [visibleCount, setVisibleCount] = React.useState(0)
  const LIST_BATCH = 6
  React.useEffect(() => {
    // Debug temporário para validar acesso ao módulo de sinais por role/usuário
    try {
      // eslint-disable-next-line no-console
      console.log('[SignalsDebug] role=', profile?.role, 'email=', user?.email, 'enabled=', signalsEnabled)
    } catch {}
  }, [signalsEnabled, profile?.role, user?.email])

  const handleCopyRequested = React.useCallback((id: string) => {
    setCopyModeById((prev) => ({
      ...prev,
      [id]: true,
    }))
  }, [])

  const handleCopyClose = React.useCallback((id: string) => {
    setCopyModeById((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  // Define quantos cards devem ser exibidos inicialmente,
  // evitando carregar toda a lista de uma vez.
  React.useEffect(() => {
    if (!listItems.length) {
      setVisibleCount(0)
      return
    }
    setVisibleCount((current) => {
      if (!current) return Math.min(LIST_BATCH, listItems.length)
      if (current > listItems.length) return listItems.length
      return current
    })
  }, [listItems.length])
  const hasMoreToShow = visibleCount < listItems.length
  const visibleItems = React.useMemo(
    () => (visibleCount ? listItems.slice(0, visibleCount) : listItems),
    [listItems, visibleCount],
  )
  const handleLoadMore = React.useCallback(() => {
    setVisibleCount((current) => Math.min(current + LIST_BATCH, listItems.length))
  }, [listItems.length])

  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [autoLoadEnabled, setAutoLoadEnabled] = React.useState(true)

  // Observa o "sentinela" no final da lista para carregar mais cards automaticamente.
  React.useEffect(() => {
    if (!hasMoreToShow) return

    const el = sentinelRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      // Se não houver suporte a IntersectionObserver, não faz nada aqui.
      setAutoLoadEnabled(false)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        })
      },
      { rootMargin: '120px 0px 0px 0px' },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [handleLoadMore, hasMoreToShow])

  const visibleIds = React.useMemo(
    () =>
      visibleItems
        .map((it) => Number(it.id))
        .filter((id) => Number.isFinite(id)),
    [visibleItems],
  )
  const { map: visibleCommunitySummaryMap } = useCommunitySummary(
    visibleIds,
    conversationOnly ? false : communityEnabled,
  )
  const communitySummaryMap = conversationOnly ? conversationSummaryMap : visibleCommunitySummaryMap

  // Mapa de zona de entrada (dentro/fora) baseado no módulo de Sinais
  React.useEffect(() => {
      if (!signalsEnabled) {
        setEntryZoneMap({})
        setMultiTfStateMap({})
        setMultiTfDirectionMap({})
        return
      }
      if (!activeIds.length) {
        setEntryZoneMap({})
        setMultiTfStateMap({})
        setMultiTfDirectionMap({})
        return
      }

      let cancelled = false

      ;(async () => {
        try {
          const [entry, multi, conflicts] = await Promise.all([
            fetchEntryZoneForOpportunities(activeIds),
            fetchSignalsMultiTfState(activeIds),
            fetchSignalsMultiTfDirectionConflicts(activeIds),
          ])
          if (!cancelled) {
            setEntryZoneMap(entry)
            const nextStates: Record<string, OpportunityMultiTfState['state']> = {}
            Object.entries(multi).forEach(([key, value]) => {
              nextStates[key] = value.state
            })
            setMultiTfStateMap(nextStates)
            setMultiTfDirectionMap(conflicts)
          }
        } catch {
          if (!cancelled) {
            setEntryZoneMap({})
            setMultiTfStateMap({})
            setMultiTfDirectionMap({})
          }
        }
      })()

      return () => {
        cancelled = true
      }
  }, [signalsEnabled, activeIds])

  // Foco centralizado (SSOT)
  const focusId = useFocusStore((s) => s.focusId)
  const highlightIds = useFocusStore((s) => s.highlightIds)
  const clearFocus = useFocusStore((s) => s.clearFocus)
  const removeHighlight = useFocusStore((s) => s.removeHighlight)

  // Quando a lista estiver carregada, faÃ§a scroll confiÃ¡vel atÃ© o card focado
  React.useEffect(() => {
    if (!focusId || loading) return
    let cancelled = false
    let attempts = 0
    const maxAttempts = 120 // ~2s com rAF + fallback

    function tryScroll() {
      if (cancelled) return
      const el = document.getElementById(`opp-card-${focusId}`)
      if (el) {
        try {
          // 1) Tenta centralizar na janela (iOS PWA Ã s vezes ignora scrollIntoView)
          const rect = el.getBoundingClientRect()
          const target = Math.max(0, Math.floor(rect.top + window.scrollY - (window.innerHeight / 2 - rect.height / 2)))
          window.scrollTo({ top: target, behavior: 'smooth' })
        } catch {}
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch {}
        return
      }
      if (attempts++ < maxAttempts) {
        requestAnimationFrame(tryScroll)
      } else {
        setTimeout(tryScroll, 200)
      }
    }
    const id = setTimeout(tryScroll, 0)
    // Fallback: se nao encontrarmos o card apos uma janela de tempo, abre o modal direto
    const fb = setTimeout(() => {
      if (cancelled) return
      const el = document.getElementById(`opp-card-${focusId}`)
      if (!el) {
        try { openModal({ id: String(focusId), symbol: '', kind: 'opportunity' }) } catch {}
      }
    }, 8000)
    return () => { cancelled = true; try { clearTimeout(id); clearTimeout(fb) } catch {} }
  }, [focusId, loading, listItems.length])

  const participatingCount = conversationSections.participating.length
  const otherCount = conversationSections.others.length

  const visibleParticipatingItems = conversationOnly
    ? visibleItems.slice(0, Math.min(visibleItems.length, participatingCount))
    : []

  const visibleOtherItems = conversationOnly ? visibleItems.slice(participatingCount) : []

  const renderMobileOpportunity = (it: OpportunityItem) => {
    const highlightCard =
      String(focusId) === String(it.id) || highlightIds.includes(String(it.id))

    const handleViewed = (id?: string | number) => {
      const sid = String(id || it.id)
      if (String(focusId) === sid) clearFocus(sid)
      try {
        removeHighlight(sid)
      } catch {}
    }

    if (isMaster || signalsEnabled) {
      return (
        <OpportunitySignalsRow
          key={it.id}
          item={it}
          thermoAllowed={thermoAllowed}
          noteForMaster={masterNote}
          signalsEnabled={isMaster ? true : !!signalsEnabled}
          inOutEnabled={isMaster ? true : !!inOutEnabled}
          highlight={highlightCard}
          multiTfState={multiTfStateMap[String(it.id)]}
          multiTfDirection={multiTfDirectionMap[String(it.id)]}
          communityEnabled={!!communityEnabled}
          communitySummary={communitySummaryMap[String(it.id)] ?? null}
          onViewed={handleViewed}
          onOpenActions={() => {
            setActionItem(it)
            setActionOpen(true)
          }}
          copyMode={!!copyModeById[String(it.id)]}
          onCopyRequested={handleCopyRequested}
          onCopyClose={handleCopyClose}
        />
      )
    }

    return (
      <OpportunityCompactCard
        key={it.id}
        item={it}
        thermoAllowed={thermoAllowed}
        noteForMaster={masterNote}
        onClick={() => {
          setActionItem(it)
          setActionOpen(true)
        }}
        highlight={highlightCard}
        communityEnabled={!!communityEnabled}
        communitySummary={communitySummaryMap[String(it.id)] ?? null}
      />
    )
  }

  const renderDesktopOpportunity = (it: OpportunityItem) => {
    const highlightCard =
      String(focusId) === String(it.id) || highlightIds.includes(String(it.id))

    const handleViewed = (id?: string | number) => {
      const sid = String(id || it.id)
      if (String(focusId) === sid) clearFocus(sid)
      try { removeHighlight(sid) } catch {}
    }

    return isMaster || signalsEnabled ? (
      <OpportunitySignalsRow
        key={it.id}
        item={it}
        thermoAllowed={thermoAllowed}
        noteForMaster={masterNote}
        signalsEnabled={isMaster ? true : !!signalsEnabled}
        inOutEnabled={isMaster ? true : !!inOutEnabled}
        highlight={highlightCard}
        multiTfState={multiTfStateMap[String(it.id)]}
        multiTfDirection={multiTfDirectionMap[String(it.id)]}
        communityEnabled={!!communityEnabled}
        communitySummary={communitySummaryMap[String(it.id)] ?? null}
        onViewed={handleViewed}
        copyMode={!!copyModeById[String(it.id)]}
        onCopyRequested={handleCopyRequested}
        onCopyClose={handleCopyClose}
        resultsEnabled={resultsEnabled === true}
        onOpenResults={(opp) =>
          openResultsDrawer({
            opportunityId: Number(opp.id),
            symbol: opp.symbol,
            name: opp.name,
            logoUrl: opp.logo_url,
          })
        }
      />
    ) : (
      <OpportunityRow
        key={it.id}
        item={it}
        thermoAllowed={thermoAllowed}
        noteForMaster={masterNote}
        signalsEnabled={false}
        highlight={highlightCard}
        onViewed={handleViewed}
        communityEnabled={!!communityEnabled}
        communitySummary={communitySummaryMap[String(it.id)] ?? null}
        resultsEnabled={resultsEnabled === true}
        onOpenResults={(opp) =>
          openResultsDrawer({
            opportunityId: Number(opp.id),
            symbol: opp.symbol,
            name: opp.name,
            logoUrl: opp.logo_url,
          })
        }
      />
    )
  }

  return (
    <InOutFeedbackProvider>
    <div className="w-full">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-lg font-semibold">
            {conversationOnly ? 'Conversas' : mode === 'in' ? 'Oportunidades em acompanhamento' : 'Oportunidades Confirmadas!'}
          </div>
          {!exceeded && (
            <div className="text-xs text-[#434343]">
              {mode === 'in'
                ? 'Visualize abaixo as oportunidades que você marcou como "Estou dentro".'
                : 'Visualize abaixo moedas com oportunidades validadas'}
            </div>
          )}
        </div>
        {!exceeded && (
          <div>
            <Button variant="outline" className="h-8 px-3 text-xs" onClick={() => setFiltersOpen(true)}>Filtrar</Button>
          </div>
        )}
      </div>

      {upgradeHint}

      {exceeded && (
        <div className="mb-3 rounded-md border border-amber-400/30 bg-amber-400/10 p-3 text-sm text-amber-300">
          VocÃª atingiu o limite de oportunidades do ciclo atual.
          {nextResetAt ? (<span> Reinicia em: {new Date(nextResetAt).toLocaleString()}</span>) : null}
          <button type="button" className="ml-3 inline-flex items-center rounded bg-primary px-2 py-1 text-[12px]"
            onClick={()=> { window.location.href = '/my-plans' }}>Fazer upgrade</button>
        </div>
      )}

      {/* BTC higher TF correction warning (D/W/M) */}
      {!exceeded && btcWarningEnabled && btc.show && btc.label && (
        <BTCWarningCard timeframeLabel={btc.label} />
      )}

      {/* Drawer de filtros (direita) */}
      <OportunidadesFilterDrawer open={filtersOpen} onOpenChange={setFiltersOpen} />

      {/* Classe para borda verde limÃ£o permanente enquanto focado */}
      

      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      {loading || (conversationOnly && communityEnabled === true && !conversationSummaryInitialized) ? (
        <div className="text-sm text-[#434343]">Carregando...</div>
      ) : activeItems.length === 0 ? (
        <div className="text-sm text-[#434343]">Nenhuma oportunidade no momento.</div>
      ) : conversationOnly && listItems.length === 0 ? (
        <div className="text-sm text-[#434343]">Nenhuma oportunidade com conversa ativa no momento.</div>
      ) : (
        <InOutProvider opportunityIds={activeIds}>
          <div className="relative flex flex-col gap-4">
          {/* Mobile/Tablet: lista em uma coluna */}
          <div className="flex flex-col gap-3 lg:hidden">
            {conversationOnly ? (
              <>
                <ConversationSectionHeader title="Participando" />
                {participatingCount === 0 ? (
                  <div className="text-sm text-[#434343]">
                    Voce ainda nao participa de nenhuma conversa ativa no momento.
                  </div>
                ) : null}
                {visibleParticipatingItems.map(renderMobileOpportunity)}

                <ConversationSectionHeader className="mt-4" title="Outras conversas" />
                {otherCount === 0 ? (
                  <div className="text-sm text-[#434343]">
                    Nenhuma outra conversa ativa no momento.
                  </div>
                ) : null}
                {visibleOtherItems.map(renderMobileOpportunity)}
              </>
            ) : (
              visibleItems.map(renderMobileOpportunity)
            )}
          </div>

          {/* Desktop (lg+): grid com 2 cards por linha em resoluções até ~1366px e 3 cards acima disso */}
          {conversationOnly ? (
            <div className="hidden lg:flex lg:flex-col lg:gap-4">
              <ConversationSectionHeader title="Participando" />
              {participatingCount === 0 ? (
                <div className="text-sm text-[#434343]">
                  Voce ainda nao participa de nenhuma conversa ativa no momento.
                </div>
              ) : (
                <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4">
                  {visibleParticipatingItems.map(renderDesktopOpportunity)}
                </div>
              )}

              <ConversationSectionHeader className="mt-4" title="Outras conversas" />
              {otherCount === 0 ? (
                <div className="text-sm text-[#434343]">
                  Nenhuma outra conversa ativa no momento.
                </div>
              ) : (
                <div className="grid grid-cols-2 2xl:grid-cols-3 gap-4">
                  {visibleOtherItems.map(renderDesktopOpportunity)}
                </div>
              )}
            </div>
          ) : (
            <div className="hidden lg:grid lg:grid-cols-2 2xl:grid-cols-3 lg:gap-4">
              {visibleItems.map(renderDesktopOpportunity)}
            </div>
          )}

          {hasMoreToShow &&
            (autoLoadEnabled ? (
              <div
                ref={sentinelRef}
                className="mt-4 flex w-full items-center justify-center py-2"
              >
                <LoadingCandles
                  className="min-h-[80px]"
                  label="Carregando mais oportunidades..."
                />
              </div>
            ) : (
              <div className="mt-2 flex w-full items-center justify-center">
                <Button
                  variant="outline"
                  className="h-8 px-4 text-xs"
                  onClick={handleLoadMore}
                >
                  Carregar mais oportunidades
                </Button>
              </div>
            ))}
          </div>

          {/* Bottom actions sheet (mobile) */}
          <OpportunityActionSheet
            open={actionOpen}
              onOpenChange={setActionOpen}
              item={actionItem}
              resultsEnabled={resultsEnabled === true}
              inOutEnabled={isMaster ? true : !!inOutEnabled}
            onViewed={(id)=> { const sid = String(id); if (sid===String(focusId)) clearFocus(sid); try { removeHighlight(sid) } catch {} }}
            onCopyRequested={handleCopyRequested}
          />
        </InOutProvider>
      )}
    </div>
    </InOutFeedbackProvider>
  )
}

function ConversationSectionHeader({
  title,
  className,
}: {
  title: string
  className?: string
}) {
  const cls = ['flex items-center', className].filter(Boolean).join(' ')
  return (
    <div className={cls}>
      <div className="text-sm font-semibold text-foreground">{title}</div>
    </div>
  )
}

function OpportunityRowCompact({
  item,
  thermoAllowed,
  noteForMaster,
  onOpenActions,
  highlight,
  communityEnabled,
  communitySummary,
}: {
  item: OpportunityItem
  thermoAllowed: boolean
  noteForMaster?: string
  onOpenActions?: () => void
  highlight?: boolean
  communityEnabled?: boolean
  communitySummary?: CommunitySummary | null
}) {
  return (
    <div
      id={`opp-card-${item.id}`}
      role="button"
      onClick={() => onOpenActions?.()}
      className={[
        'relative cursor-pointer rounded-xl border bg-[#1c1c1c] p-3 min-h-[110px] md:min-h-[110px] active:bg-[#171717]',
        highlight ? 'ring-2 ring-lime-400 border-lime-400' : 'border-[#1c1c1c]',
      ].join(' ')}
    >
      <GlowingEffect
        spread={40}
        glow
        disabled={false}
        proximity={64}
        inactiveZone={0.01}
        borderWidth={3}
      />
      {/* Outer grid: 3 columns (logo | content | action), 3 rows */}
      <div className="grid grid-cols-[auto,1fr,auto] grid-rows-[auto,auto,auto] items-center gap-x-3 gap-y-2">
        {/* Col 1: logo (spans 3 rows) */}
        <div className="row-span-3 col-start-1 flex items-center justify-start">
          <div className="inline-flex items-center rounded p-1">
            {item.logo_url ? (
              <img src={item.logo_url} alt={item.symbol} className="h-[2.65rem] w-[2.65rem] flex-shrink-0 rounded-full object-cover" />
            ) : (
              <div className="h-[2.65rem] w-[2.65rem] flex-shrink-0 rounded-full bg-muted" />
            )}
          </div>
        </div>

        {/* Row 1: esquerda = nome/sigla; direita = objetivo + Ã­cone de status */}
        <div className="col-start-2 col-span-1">
          <div className="min-w-0 flex items-baseline gap-2">
            <span className="truncate text-[13px] font-medium leading-tight">{item.name || item.symbol}</span>
            <span className="truncate text-[11px] text-foreground/70 leading-tight">| {formatPair(item.symbol)}</span>
          </div>
        </div>
        <div className="col-start-3 row-start-1 flex items-center justify-end gap-2">
          <span className={[
            'whitespace-nowrap text-[12px] font-medium',
            item.objective === 'Long' ? 'text-emerald-400' : item.objective === 'Short' ? 'text-red-400' : 'text-foreground/80',
          ].join(' ')}>
            {item.objective === 'Long' ? 'bullish' : item.objective === 'Short' ? 'bearish' : '-'}
          </span>
          {/* Bull/Bear icon near objective (mobile) */}
          <ObjectiveIcon objective={item.objective} />
          {item.nearBreak ? (
            <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
          ) : item.uiWarning ? (
            <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-amber-400" />
          ) : String(item.status || '').toLowerCase() === 'active' ? (
            <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-emerald-400" />
          ) : (
            <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-amber-400" />
          )}
        </div>

        {/* Row 2, Col 2: progress bar */}
        <div className="col-start-2 col-span-2 mt-1">
          <div className="relative">
            <ThermometerWithGate enabled={thermoAllowed} value={item.thermometerPercent ?? item.confluencePercent} className="h-2.5 w-full" note={noteForMaster} noteClassName="text-[10px] md:text-xs" />
            {thermoAllowed && (
              <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[11px] font-medium text-foreground/80">
                {(item.thermometerPercent ?? item.confluencePercent)}%
              </span>
            )}
          </div>
        </div>

        {/* Row 2.5: Comunidade (abaixo do termômetro) */}
        {communityEnabled && (
          <div className="col-start-2 col-span-2 mt-1">
            <OpportunityCommunityEntry
              opportunityId={Number(item.id)}
              symbol={item.symbol}
              name={item.name}
              logoUrl={item.logo_url}
              enabled={!!communityEnabled}
              summary={communitySummary ?? null}
            />
          </div>
        )}

        {/* Row 3: Ã  esquerda timeframes; Ã  direita recommendation */}
        <div className="col-start-2 col-span-2">
          <div className="min-w-0 flex items-center justify-between">
            <div className="min-w-0 whitespace-nowrap text-[12px] text-foreground/80">
              {[item.fractalLower, item.fractalUpper].filter(Boolean).join(' / ') || '-'}
            </div>
            <div className="min-w-0 whitespace-nowrap text-[12px] text-foreground/80 text-right">
              {item.recommendation || '-'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function OpportunityRow({
  item,
  thermoAllowed,
  noteForMaster,
  signalsEnabled,
  highlight,
  onViewed,
  communityEnabled,
  communitySummary,
  resultsEnabled,
  onOpenResults,
}: {
  item: OpportunityItem
  thermoAllowed: boolean
  noteForMaster?: string
  signalsEnabled: boolean
  highlight?: boolean
  onViewed?: (id: string) => void
  communityEnabled?: boolean
  communitySummary?: CommunitySummary | null
  resultsEnabled?: boolean
  onOpenResults?: (item: OpportunityItem) => void
}) {
  const { openModal, openDebug, openReport } = useOportunidadeModal()
  const { user, profile } = useAuth()
  const isMaster = isMasterByEmail(user?.email) || profile?.role === 'master'
  const { signals, loading: signalsLoading } = useOpportunitySignals(item.id, signalsEnabled)
  const hasResults = !!resultsEnabled && typeof onOpenResults === 'function'

  return (
    <div
      className={[
        'relative rounded-xl border-[4px] bg-[#1a1a1a] px-4 pt-4',
        communityEnabled ? 'pb-0' : 'pb-4',
        highlight ? 'ring-2 ring-lime-400 border-lime-400' : 'border-[#262626]',
      ].join(' ')}
    >
      {/* Efeito de borda animada (igual à landing) */}
      <GlowingEffect
        spread={40}
        glow
        disabled={false}
        proximity={64}
        inactiveZone={0.01}
        borderWidth={3}
      />
      {/* Linha superior: logo, timeframes, recomendação, objetivo, status, ações */}
      <div className="grid items-center gap-6 md:grid-cols-[1.2fr,1.1fr,0.9fr,0.6fr,0.4fr]">
        {/* Asset */}
        <div className="flex items-center gap-3">
          {item.logo_url ? (
            <img src={item.logo_url} alt={item.symbol} className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />
          ) : (
            <div className="h-8 w-8 flex-shrink-0 rounded-full bg-muted" />
          )}
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">{item.name || item.symbol}</div>
            <div className="truncate text-xs text-[#434343]">{item.symbol}</div>
          </div>
        </div>
        {/* Timeframes + tipo de oportunidade */}
        <div className="min-w-0">
          <div className="text-sm font-medium">
            {[item.fractalLower, item.fractalUpper].filter(Boolean).join(' | ') || '-'}
          </div>
          <div className="mt-0.5 truncate text-xs text-[#434343]">
            {item.recommendation || '-'}
          </div>
        </div>
        {/* Objective + trend icon */}
        <div className="flex items-center gap-2">
          <div className={[ 'text-sm font-medium', item.objective === 'Long' ? 'text-emerald-400' : item.objective === 'Short' ? 'text-red-400' : 'text-foreground/80', ].join(' ')}>
            {item.objective === 'Long' ? 'bullish' : item.objective === 'Short' ? 'bearish' : '-'}
          </div>
          <ObjectiveIcon objective={item.objective} />
        </div>
        {/* Status icon */}
        <div className="flex items-center gap-2">
          {item.nearBreak ? (
            <AlertCircle className="h-5 w-5 text-red-500" />
          ) : item.uiWarning ? (
            <AlertTriangle className="h-5 w-5 text-amber-400" />
          ) : String(item.status || '').toLowerCase() === 'active' ? (
            <CheckCircle className="h-5 w-5 text-emerald-400" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-amber-400" />
          )}
        </div>
        {/* Action */}
        <div className="flex items-center justify-end gap-2">
          {hasResults && (
            <button
              type="button"
              onClick={() => onOpenResults?.(item)}
              className="text-[11px] font-semibold text-sky-400 hover:underline"
            >
              Resultado
            </button>
          )}
          <DropdownMenu
            trigger={<button type="button" className="inline-flex items-center rounded p-1 hover:bg-muted/30"><MoreHorizontal className="h-5 w-5 text-[#434343]" /></button>}
            items={isMaster ? [
              { label: 'Detalhes', onSelect: () => { try { onViewed?.(String(item.id)) } catch {}; openModal({ id: item.id, symbol: item.symbol, name: item.name, logo_url: item.logo_url, kind: 'opportunity' }) } },
              { label: 'Relatar Bug', onSelect: () => openReport({ id: item.id, symbol: item.symbol, name: item.name, logo_url: item.logo_url, kind: 'opportunity' }) },
              { label: 'Debug', onSelect: () => openDebug({ id: item.id, symbol: item.symbol, name: item.name, logo_url: item.logo_url, kind: 'opportunity' }) },
            ] : [
              { label: 'Ver gráfico', onSelect: () => { try { onViewed?.(String(item.id)) } catch {}; openModal({ id: item.id, symbol: item.symbol, name: item.name, logo_url: item.logo_url, kind: 'opportunity' }) } },
              { label: 'Relatar Bug', onSelect: () => openReport({ id: item.id, symbol: item.symbol, name: item.name, logo_url: item.logo_url, kind: 'opportunity' }) },
            ]}
            align="end"
          />
        </div>
      </div>

      {/* Linha inferior: term“metro + bloco de Entrada / Alvos / Stop (Sinais) */}
      <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="w-full md:flex-1">
          <div className="relative w-full">
            <ThermometerWithGate
              enabled={thermoAllowed}
              value={item.thermometerPercent ?? item.confluencePercent}
              note={noteForMaster}
              className="h-2.5 w-full"
            />
            {thermoAllowed && (
              <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs font-medium text-foreground/80">
                {(item.thermometerPercent ?? item.confluencePercent)}%
              </span>
            )}
          </div>

          {communityEnabled && (
            <OpportunityCommunityEntry
              opportunityId={Number(item.id)}
              symbol={item.symbol}
              name={item.name}
              logoUrl={item.logo_url}
              enabled={!!communityEnabled}
              summary={communitySummary ?? null}
              renderFooter={(summaryRow) => (
                <OpportunityCommunityPnlRow
                  communityEnabled={!!communityEnabled}
                  summaryContent={summaryRow}
                  signals={signals ?? null}
                />
              )}
            />
          )}
        </div>
        {signalsEnabled && (
          <div className="w-full md:basis-[40%] md:pl-2">
            <AsyncContentProgress
              loading={signalsLoading && !signals}
              label="Aguarde, calculando dados..."
            >
              {signals && <SignalCard signals={signals} />}
            </AsyncContentProgress>
          </div>
        )}
      </div>

      {resultsEnabled && onOpenResults && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={() => onOpenResults(item)}
            className="text-[11px] font-semibold text-sky-400 hover:underline"
          >
            Resultado
          </button>
        </div>
      )}
    </div>
  )
}

function OpportunityRowMock() {
  return (
    <div className="rounded-xl border border-[#161616] bg-background/40 p-4">
      <div className="grid items-center gap-6 md:grid-cols-[1.2fr,0.9fr,1fr,0.8fr,0.6fr,1.4fr,0.4fr]">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="min-w-0">
            <Skeleton className="h-4 w-28" />
            <div className="mt-1"><Skeleton className="h-3 w-20" /></div>
          </div>
        </div>
        <div><Skeleton className="h-4 w-24" /></div>
        <div><Skeleton className="h-4 w-28" /></div>
        <div><Skeleton className="h-4 w-16" /></div>
        <div />
        <div className="pr-2 md:pr-4 min-w-0">
          <div className="mt-1 mb-1 flex items-center gap-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-8" />
          </div>
        </div>
        <div className="flex items-center justify-end">
          <Skeleton className="h-6 w-6 rounded" />
        </div>
      </div>
    </div>
  )
}

function OpportunityRowCompactMock() {
  return (
    <div className="rounded-xl border border-[#161616] bg-background/40 p-3 min-h-[110px] md:min-h-[110px]">
      <div className="grid grid-cols-[auto,1fr,auto] grid-rows-[auto,auto,auto] items-center gap-x-3 gap-y-2">
        <div className="row-span-3 col-start-1 flex items-center justify-start">
          <Skeleton className="h-[2.65rem] w-[2.65rem] rounded-full" />
        </div>
        <div className="col-start-2 col-span-1">
          <div className="min-w-0 flex items-baseline gap-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
        <div className="row-span-3 col-start-3 flex items-center justify-end">
          <Skeleton className="h-6 w-6 rounded" />
        </div>
        <div className="col-start-2 col-span-1">
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="col-start-2 col-span-1">
          <Skeleton className="h-3 w-40" />
        </div>
      </div>
    </div>
  )
}

function formatPair(symbol: string | undefined | null): string {
  const s = String(symbol || '').trim().toUpperCase()
  if (!s) return '-'
  const m = s.match(/^(.*?)(USDT|USDC|BTC)$/)
  if (m) return `${m[1]}/${m[2]}`
  const clean = s.replace(/[^A-Z0-9]/g, '')
  const m2 = clean.match(/^(.*?)(USDT|USDC|BTC)$/)
  if (m2) return `${m2[1]}/${m2[2]}`
  return s
}
